import { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api, { API_BASE } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Save, Mail, Link2, Unplug, RefreshCw, CheckCircle2, Trash2, AlertTriangle, ShieldOff, Search, Plus, Rss, Users, Clock, Sparkles } from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

export default function SettingsPage() {
  const { user, workspace, refresh } = useAuth();
  const [profile, setProfile] = useState({
    name: "",
    default_meeting_link: "",
    signature: "",
    phone: "",
    default_from_email: "",
    meeting_delegate_name: "",
    meeting_delegate_email: "",
    meeting_delegate_meeting_link: "",
  });
  const [saving, setSaving] = useState(false);
  const [m365, setM365] = useState({ configured: false, connected: false, connection: null });
  const [polling, setPolling] = useState(false);
  const [purging, setPurging] = useState(false);
  const [purgeOpen, setPurgeOpen] = useState(false);
  const [purgeAll, setPurgeAll] = useState(false);
  const [supp, setSupp] = useState({ suppressions: [], total: 0, shown: 0 });
  const [suppSearch, setSuppSearch] = useState("");
  const [newSupp, setNewSupp] = useState("");
  const [suppBusy, setSuppBusy] = useState(false);
  const [blog, setBlog] = useState({ endpoint_url: "", configured: false, api_key_set: false, api_key_hint: "" });
  const [blogKey, setBlogKey] = useState("");
  const [blogBusy, setBlogBusy] = useState(false);
  const [dir, setDir] = useState({ export_url: "", configured: false, api_key_set: false, api_key_hint: "" });
  const [dirKey, setDirKey] = useState("");
  const [dirBusy, setDirBusy] = useState(false);
  const [sw, setSw] = useState(null);
  const [swBusy, setSwBusy] = useState(false);
  const [cp, setCp] = useState(null);
  const [cpBusy, setCpBusy] = useState(false);
  const [fb, setFb] = useState(null);
  const [fbForm, setFbForm] = useState({ page_id: "", page_token: "", app_secret: "", app_id: "" });
  const [fbBusy, setFbBusy] = useState(false);
  const [fbPageChoice, setFbPageChoice] = useState("");
  const [fbLinkedin, setFbLinkedin] = useState("");
  useEffect(() => { if (fb?.linkedin_url !== undefined) setFbLinkedin(fb.linkedin_url || ""); }, [fb?.linkedin_url]);

  const loadFb = async () => { try { const { data } = await api.get("/content/facebook"); setFb(data); } catch { /* ignore */ } };
  useEffect(() => { loadFb(); }, []);
  const [brand, setBrand] = useState(null);
  const [brandSender, setBrandSender] = useState("");
  const [brandBusy, setBrandBusy] = useState(false);
  const loadBrand = async () => {
    try { const { data } = await api.get("/workspace/brand"); setBrand(data); setBrandSender(data?.sender_email || ""); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadBrand(); }, []);
  const saveBrandSender = async () => {
    setBrandBusy(true);
    try {
      const { data } = await api.put("/workspace/brand", { sender_email: brandSender.trim() });
      setBrand(data); setBrandSender(data?.sender_email || "");
      toast.success("Workspace sender updated");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not update sender"); }
    setBrandBusy(false);
  };
  useEffect(() => {
    const p = new URLSearchParams(window.location.search).get("fb");
    if (p === "connected") { toast.success("Facebook Page connected 🎉"); loadFb(); }
    else if (p === "pick") { toast.info(`Almost there — pick the ${workspace?.name || "correct"} Page below.`); loadFb(); }
    else if (p === "nopages") toast.error("No Page was granted. Re-connect and be sure to tick your company Page.");
    else if (p === "error") toast.error("Facebook connection failed — please try again.");
    if (p) window.history.replaceState({}, "", "/app/settings");
  }, []);
  const startFbOauth = async () => {
    setFbBusy(true);
    try { const { data } = await api.get("/content/facebook/oauth/start"); window.location.href = data.url; }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not start Facebook connect"); setFbBusy(false); }
  };
  const saveFbAppCreds = async () => {
    if (!fbForm.app_id?.trim() || !fbForm.app_secret?.trim()) { toast.error("Enter both App ID and App Secret"); return; }
    setFbBusy(true);
    try { const { data } = await api.post("/content/facebook/app-creds", { app_id: fbForm.app_id, app_secret: fbForm.app_secret }); setFb(data); toast.success("App credentials saved — now click Connect with Facebook"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setFbBusy(false);
  };
  const connectFb = async () => {
    if (!fbForm.page_token.trim()) { toast.error("Paste your Page Access Token"); return; }
    setFbBusy(true);
    try {
      const { data } = await api.post("/content/facebook/connect", fbForm);
      toast.success(`Connected to ${data.page_name || "your Facebook Page"}`);
      setFbForm({ page_id: "", page_token: "", app_secret: "" });
      await loadFb();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not connect"); }
    setFbBusy(false);
  };
  const fbPref = async (patch) => {
    setFbBusy(true);
    try { const { data } = await api.post("/content/facebook/prefs", patch); setFb(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setFbBusy(false);
  };
  const fbAction = async (path, label) => {
    setFbBusy(true);
    try { await api.post(`/content/facebook/${path}`); toast.success(label); if (path === "disconnect") await loadFb(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
    setFbBusy(false);
  };
  const selectFbPage = async () => {
    if (!fbPageChoice) { toast.error("Choose a Page first"); return; }
    setFbBusy(true);
    try {
      const { data } = await api.post("/content/facebook/select-page", { page_id: fbPageChoice });
      if (data?.needs_grant && data?.oauth_url) {
        toast.info(data.message || "Redirecting to Facebook to grant this Page…", { duration: 6000 });
        setTimeout(() => { window.location.href = data.oauth_url; }, 1200);
        return;
      }
      toast.success(`Now publishing to ${data.page_name || "your Page"} 🎉`);
      setFbPageChoice("");
      await loadFb();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not select Page"); }
    setFbBusy(false);
  };

  const loadCp = async () => { try { const { data } = await api.get("/content/pulse"); setCp(data); } catch { /* ignore */ } };
  useEffect(() => { loadCp(); }, []);
  const saveCp = async (patch) => {
    setCpBusy(true);
    try {
      const { data } = await api.post("/content/pulse/config", patch);
      setCp((c) => ({ ...c, config: data }));
      toast.success("Content Pulse saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setCpBusy(false);
  };
  const runCp = async () => {
    setCpBusy(true);
    try {
      const { data } = await api.post("/content/pulse/run");
      toast.success(`Drafted ${data.drafted?.length || 0} post(s)${data.emailed ? " · reminder email sent" : ""}`, {
        description: "Review & approve them in Content Studio.",
      });
      await loadCp();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not run"); }
    setCpBusy(false);
  };

  const loadSw = async () => {
    try { const { data } = await api.get("/system/send-window"); setSw(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadSw(); }, []);
  const saveSw = async (patch) => {
    setSwBusy(true);
    try {
      const { data } = await api.post("/system/send-window", patch);
      setSw(data);
      toast.success("Send-window rules saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setSwBusy(false);
  };

  const loadDir = async () => {
    try { const { data } = await api.get("/campaigns/directory-config"); setDir(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadDir(); }, []);
  const saveDir = async () => {
    setDirBusy(true);
    try {
      const payload = { export_url: dir.export_url || "" };
      if (dirKey.trim()) payload.api_key = dirKey.trim();
      const { data } = await api.post("/campaigns/directory-config", payload);
      setDir(data); setDirKey("");
      toast.success("Partner directory saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setDirBusy(false);
  };

  const loadBlog = async () => {
    try { const { data } = await api.get("/articles/blog-config"); setBlog(data); } catch { /* ignore */ }
  };
  useEffect(() => { loadBlog(); }, []);
  const saveBlog = async () => {
    setBlogBusy(true);
    try {
      const payload = { endpoint_url: blog.endpoint_url || "" };
      if (blogKey.trim()) payload.api_key = blogKey.trim();
      const { data } = await api.post("/articles/blog-config", payload);
      setBlog(data); setBlogKey("");
      toast.success("Blog ingestion saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    setBlogBusy(false);
  };

  const loadSuppressions = async (search = "") => {
    try {
      const { data } = await api.get(`/system/suppressions${search ? `?search=${encodeURIComponent(search)}` : ""}`);
      setSupp(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { loadSuppressions(); }, []);
  // Debounced search
  useEffect(() => {
    const t = setTimeout(() => loadSuppressions(suppSearch), 350);
    return () => clearTimeout(t);
  }, [suppSearch]);

  const addSuppression = async () => {
    const email = newSupp.trim().toLowerCase();
    if (!email.includes("@")) { toast.error("Enter a valid email"); return; }
    setSuppBusy(true);
    try {
      await api.post("/system/suppressions", { email, reason: "manual" });
      toast.success(`${email} added to suppression list`);
      setNewSupp("");
      loadSuppressions(suppSearch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add");
    }
    setSuppBusy(false);
  };

  const removeSuppression = async (email) => {
    try {
      await api.delete(`/system/suppressions/${encodeURIComponent(email)}`);
      toast.success(`${email} re-subscribed`);
      loadSuppressions(suppSearch);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not remove");
    }
  };

  useEffect(() => {
    if (user) {
      setProfile({
        name: user.name || "",
        default_meeting_link: user.default_meeting_link || "",
        signature: user.signature || "",
        phone: user.phone || "",
        default_from_email: user.default_from_email || "",
        meeting_delegate_name: user.meeting_delegate_name || "",
        meeting_delegate_email: user.meeting_delegate_email || "",
        meeting_delegate_meeting_link: user.meeting_delegate_meeting_link || "",
      });
    }
  }, [user]);

  const loadM365 = async () => {
    try {
      const { data } = await api.get("/m365/status");
      setM365(data);
    } catch { /* ignore */ }
  };
  useEffect(() => {
    loadM365();
    // Re-check on M365 callback (after returning from Microsoft)
    const params = new URLSearchParams(window.location.search);
    if (params.get("m365") === "connected") {
      toast.success("Microsoft 365 connected");
      window.history.replaceState({}, "", "/app/settings");
      setTimeout(loadM365, 500);
    }
  }, []);

  const connectM365 = () => {
    const token = localStorage.getItem("lg_token");
    if (!token) { toast.error("Login first"); return; }
    // Redirect browser directly so Microsoft can take over
    window.location.href = `${API_BASE}/auth/m365/start?token=${encodeURIComponent(token)}`;
  };

  const disconnectM365 = async () => {
    if (!confirm("Disconnect Microsoft 365 from this workspace?")) return;
    try {
      await api.post("/m365/disconnect");
      toast.success("Disconnected");
      loadM365();
    } catch { toast.error("Disconnect failed"); }
  };

  const [recentEmails, setRecentEmails] = useState([]);

  const loadRecentEmails = async () => {
    try {
      const { data } = await api.get("/m365/inbox?limit=10");
      setRecentEmails(data.messages || []);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    if (m365.connected) loadRecentEmails();
  }, [m365.connected]);

  const pollInbox = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/m365/poll-inbox");
      toast.success(`Ingested ${data.ingested} new (fetched ${data.fetched})`);
      await loadRecentEmails();
    } catch (e) {
      toast.error(`Inbox poll failed: ${e?.response?.data?.detail || "unknown"}`);
    }
    setPolling(false);
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/auth/me", profile);
      await refresh();
      toast.success("Profile updated");
    } catch { toast.error("Update failed"); }
    setSaving(false);
  };

  const runPurge = async () => {
    setPurging(true);
    try {
      const { data } = await api.post("/system/purge-demo-data", {
        confirm: true,
        include_real_leads: purgeAll,
      });
      const counts = Object.entries(data.deleted || {})
        .filter(([, v]) => v > 0)
        .map(([k, v]) => `${v} ${k}`)
        .join(", ");
      toast.success(counts ? `Data reset complete — removed ${counts}.` : "Workspace is already clean — nothing to remove.");
      setPurgeOpen(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(detail || "Purge failed — owner permission required.");
    }
    setPurging(false);
  };

  return (
    <DashboardLayout
      eyebrow="Settings"
      title="Workspace & profile settings"
      actions={
        <Button onClick={save} disabled={saving} data-testid="settings-save-btn" className="rounded-sm">
          <Save className="w-4 h-4 mr-1.5" />{saving ? "Saving..." : "Save"}
        </Button>
      }
    >
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="data-card space-y-3">
          <div className="label-eyebrow mb-1">Workspace</div>
          <Row label="Name"><Input data-testid="settings-workspace-name" value={workspace?.name || ""} readOnly /></Row>
          <Row label="Slug"><Input value={workspace?.slug || ""} readOnly /></Row>
          <Row label="Plan"><Input value={`${workspace?.plan || ""} · ${workspace?.plan_status || ""}`} readOnly /></Row>
          <Row label="Automatic sender mailbox (Microsoft 365 — used by autopilot & one-off sends)">
            <div className="flex gap-2">
              <Input
                data-testid="settings-brand-sender"
                value={brandSender}
                onChange={(e) => setBrandSender(e.target.value)}
                placeholder="Logi_Growth@cargo-cfs.com"
              />
              <Button
                data-testid="settings-brand-sender-save"
                onClick={saveBrandSender}
                disabled={brandBusy || brandSender.trim() === (brand?.sender_email || "")}
                variant="secondary"
                className="rounded-sm shrink-0"
              >{brandBusy ? "Saving..." : "Save"}</Button>
            </div>
            <p className="text-[0.6rem] text-muted-foreground mt-1">
              Must be a real, licensed Exchange Online (cloud) mailbox in your M365 tenant. On-premise, unlicensed or inactive mailboxes are rejected by Microsoft (MailboxNotEnabledForRESTAPI). Leave blank to fall back to the server default.
            </p>
          </Row>
        </div>

        <div className="data-card space-y-3">
          <div className="label-eyebrow mb-1">Your profile</div>
          <Row label="Name">
            <Input
              data-testid="settings-name"
              value={profile.name}
              onChange={(e) => setProfile({ ...profile, name: e.target.value })}
            />
          </Row>
          <Row label="Email">
            <Input value={user?.email || ""} readOnly />
          </Row>
          <Row label="Phone (optional)">
            <Input
              data-testid="settings-phone"
              value={profile.phone}
              onChange={(e) => setProfile({ ...profile, phone: e.target.value })}
              placeholder="+386 ..."
            />
          </Row>
        </div>

        <div className="lg:col-span-2 data-card space-y-3">
          <div className="label-eyebrow mb-1">Outreach defaults</div>
          <p className="text-xs text-muted-foreground">
            AI uses these when drafting messages: your meeting link is auto-injected when proposing a call, your signature appends to email drafts, and emails are sent FROM the address below.
          </p>
          <Row label="Send emails AS (Microsoft 365 alias — requires 'Send As' permission)">
            <Input
              data-testid="settings-from-email"
              value={profile.default_from_email}
              onChange={(e) => setProfile({ ...profile, default_from_email: e.target.value })}
              placeholder="LogiGrowth@partner.europacific.com"
            />
            <p className="text-[0.6rem] text-muted-foreground mt-1">
              Leave blank to send from your own mailbox ({user?.email || ""}). To send from a different address, your M365 admin must grant you &apos;Send As&apos; permission on that mailbox.
            </p>
          </Row>
          <Row label="Default meeting link (if no delegate is set below)">
            <Input
              data-testid="settings-meeting-link"
              value={profile.default_meeting_link}
              onChange={(e) => setProfile({ ...profile, default_meeting_link: e.target.value })}
              placeholder="https://teams.microsoft.com/l/meetup-join/... or https://calendar.app.google/..."
            />
          </Row>
          <Row label="Email signature">
            <textarea
              data-testid="settings-signature"
              value={profile.signature}
              onChange={(e) => setProfile({ ...profile, signature: e.target.value })}
              rows={4}
              className="w-full px-3 py-2 text-sm bg-background border border-border rounded-sm focus:outline-none focus:ring-1 focus:ring-signal-500"
              placeholder={`Best regards,\nYour Name\nEuropacific · Freight forwarding & customs\n+386 ... | europacific.com`}
            />
          </Row>
        </div>

        <div className="lg:col-span-2 data-card space-y-3" data-testid="meeting-delegate-card">
          <div className="label-eyebrow mb-1">Meeting delegate (handover for calls)</div>
          <p className="text-xs text-muted-foreground">
            When AI replies to an email and the sender asks for a meeting/call, it will introduce this person instead of proposing times directly. Leave blank to handle meetings yourself.
          </p>
          <div className="grid md:grid-cols-2 gap-3">
            <Row label="Delegate name">
              <Input
                data-testid="settings-delegate-name"
                value={profile.meeting_delegate_name}
                onChange={(e) => setProfile({ ...profile, meeting_delegate_name: e.target.value })}
                placeholder="Kristina Dobravec"
              />
            </Row>
            <Row label="Delegate email">
              <Input
                data-testid="settings-delegate-email"
                value={profile.meeting_delegate_email}
                onChange={(e) => setProfile({ ...profile, meeting_delegate_email: e.target.value })}
                placeholder="kristina.dobravec@europacific.com"
              />
            </Row>
          </div>
          <Row label="Delegate's meeting link (Teams / Meet / Calendly — used by AI when proposing calls)">
            <Input
              data-testid="settings-delegate-meeting-link"
              value={profile.meeting_delegate_meeting_link}
              onChange={(e) => setProfile({ ...profile, meeting_delegate_meeting_link: e.target.value })}
              placeholder="https://teams.microsoft.com/l/meetup-join/..."
            />
          </Row>
        </div>

        <div className="lg:col-span-2 data-card space-y-3" data-testid="m365-card">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-signal-500" />
              <span className="label-eyebrow">Microsoft 365 / Entra mailbox</span>
              {m365.connected && (
                <span className="px-2 py-0.5 rounded-sm text-[0.6rem] tracking-widest uppercase font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-500 inline-flex items-center gap-1">
                  <CheckCircle2 className="w-3 h-3" /> connected
                </span>
              )}
              {!m365.configured && (
                <span className="px-2 py-0.5 rounded-sm text-[0.6rem] tracking-widest uppercase font-mono border border-destructive/40 bg-destructive/10 text-destructive">
                  not configured on server
                </span>
              )}
            </div>
            {m365.connected ? (
              <div className="flex gap-2">
                <Button size="sm" variant="outline" onClick={pollInbox} disabled={polling} data-testid="m365-poll-btn">
                  <RefreshCw className={`w-3.5 h-3.5 mr-1.5 ${polling ? "animate-spin" : ""}`} />
                  {polling ? "Polling..." : "Poll inbox"}
                </Button>
                <Button size="sm" variant="ghost" onClick={disconnectM365} data-testid="m365-disconnect-btn" className="text-destructive">
                  <Unplug className="w-3.5 h-3.5 mr-1.5" /> Disconnect
                </Button>
              </div>
            ) : (
              <Button size="sm" onClick={connectM365} disabled={!m365.configured} data-testid="m365-connect-btn">
                <Link2 className="w-3.5 h-3.5 mr-1.5" /> Connect Microsoft 365
              </Button>
            )}
          </div>
          {m365.connected && m365.connection && (
            <div className="text-xs text-muted-foreground space-y-1 pt-2 border-t border-border">
              <div><span className="font-mono">{m365.connection.mail}</span> · {m365.connection.display_name}</div>
              <div>Connected {new Date(m365.connection.connected_at).toLocaleString()}</div>
              {m365.connection.status !== "connected" && (
                <div className="text-amber-accent">⚠ Status: {m365.connection.status} — please reconnect</div>
              )}
            </div>
          )}
          {m365.connected && (
            <div className="pt-3 border-t border-border" data-testid="m365-recent-emails">
              <div className="label-eyebrow mb-2">Last polled emails ({recentEmails.length})</div>
              {recentEmails.length === 0 ? (
                <p className="text-xs text-muted-foreground italic">
                  No emails ingested yet. Click &quot;Poll inbox&quot; to fetch the latest from your Microsoft 365 mailbox.
                </p>
              ) : (
                <ul className="space-y-2">
                  {recentEmails.map((m) => (
                    <li key={m.id} className="text-xs border border-border bg-background rounded-sm p-2.5" data-testid={`m365-email-${m.id}`}>
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <span className="font-medium truncate">{m.subject || "(no subject)"}</span>
                        <span className="text-muted-foreground whitespace-nowrap">{m.received_at ? new Date(m.received_at).toLocaleString() : ""}</span>
                      </div>
                      <div className="text-muted-foreground font-mono text-[0.65rem] truncate mt-0.5">from: {m.from_email}</div>
                      {m.body_preview && (
                        <div className="text-muted-foreground mt-1 line-clamp-2">{m.body_preview}</div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
          {!m365.connected && (
            <p className="text-xs text-muted-foreground">
              Connect your O365 mailbox to send personalized AI outreach via your real address — emails appear in your Sent Items, replies auto-route to LogiGrowth Inbox.
            </p>
          )}
        </div>

        <div className="lg:col-span-2 data-card space-y-3" data-testid="send-window-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-signal-500" />
              <span className="label-eyebrow">Cold-email send window</span>
            </div>
            {sw && (
              <span
                data-testid="send-window-status"
                className={`text-[0.7rem] px-2 py-1 rounded-sm border ${sw.can_send_now
                  ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                  : "border-amber-accent/40 text-amber-accent bg-amber-accent/10"}`}>
                {sw.can_send_now ? "● Sending allowed now" : `⏸ Deferred — ${(sw.reason || "").replace(/_/g, " ")}`}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            The autonomous sender only sends <strong>cold emails</strong> during these hours. Outside the window
            (nights, weekends, Slovenian public holidays) emails are <strong>deferred to the next allowed slot — never lost</strong>.
            LinkedIn invites/DMs are unaffected.
          </p>
          {sw ? (
            <>
              <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Enable send window</div>
                  <div className="text-[0.7rem] text-muted-foreground">When off, emails can send any time.</div>
                </div>
                <Button size="sm" variant={sw.enabled ? "default" : "outline"} disabled={swBusy}
                  data-testid="send-window-enable-toggle" className="rounded-sm"
                  onClick={() => saveSw({ enabled: !sw.enabled })}>
                  {sw.enabled ? "On" : "Off"}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Start hour (24h)</Label>
                  <Input type="number" min={0} max={23} data-testid="send-window-start"
                    value={sw.start_hour} onChange={(e) => setSw({ ...sw, start_hour: parseInt(e.target.value || "0") })} />
                </div>
                <div>
                  <Label className="text-xs">End hour (24h)</Label>
                  <Input type="number" min={1} max={24} data-testid="send-window-end"
                    value={sw.end_hour} onChange={(e) => setSw({ ...sw, end_hour: parseInt(e.target.value || "0") })} />
                </div>
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                <div className="text-sm">Skip Slovenian public holidays</div>
                <Button size="sm" variant={sw.skip_holidays ? "default" : "outline"} disabled={swBusy}
                  data-testid="send-window-holidays-toggle" className="rounded-sm"
                  onClick={() => saveSw({ skip_holidays: !sw.skip_holidays })}>
                  {sw.skip_holidays ? "On" : "Off"}
                </Button>
              </div>
              <div className="text-[0.7rem] text-muted-foreground">
                Timezone <strong>{sw.tz}</strong> · Days <strong>{(sw.day_names || []).join(", ")}</strong> · Local time now {sw.local_time}
              </div>
              {sw.upcoming_holidays?.length > 0 && (
                <div className="text-[0.7rem] text-muted-foreground">
                  Next holidays skipped: {sw.upcoming_holidays.map((h) => `${h.date} (${h.name})`).slice(0, 3).join(" · ")}
                </div>
              )}
              <div>
                <Button size="sm" onClick={() => saveSw({ start_hour: sw.start_hour, end_hour: sw.end_hour })}
                  disabled={swBusy} data-testid="send-window-save-btn" className="rounded-sm">
                  <Save className="w-3.5 h-3.5 mr-1.5" />{swBusy ? "Saving…" : "Save hours"}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground italic">Loading…</p>
          )}
        </div>

        <div className="lg:col-span-2 data-card space-y-3" data-testid="content-pulse-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <Rss className="w-4 h-4 text-signal-500" />
              <span className="label-eyebrow">Content Pulse — weekly LinkedIn cadence</span>
            </div>
            {cp?.status && (
              <span data-testid="content-pulse-status"
                className={`text-[0.7rem] px-2 py-1 rounded-sm border ${cp.status.on_track
                  ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                  : "border-amber-accent/40 text-amber-accent bg-amber-accent/10"}`}>
                {cp.status.ready}/{cp.status.target} ready{cp.status.pending_count ? ` · ${cp.status.pending_count} to approve` : ""}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Keeps Europacific posting every week to grow followers &amp; beat competitors. AI auto-drafts any missing posts and
            emails a reminder (<strong>Mon &amp; Thu</strong>). Approve them in Content Studio — or turn on <strong>Autopilot</strong> to let LogiGrowth publish for you.
          </p>
          {cp?.config ? (
            <>
              <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                <div>
                  <div className="text-sm font-medium">Autopilot</div>
                  <div className="text-[0.7rem] text-muted-foreground">When ON, approved posts auto-publish to LinkedIn ({cp.config.posts_per_run}/reminder).</div>
                </div>
                <Button size="sm" variant={cp.config.autopilot ? "default" : "outline"} disabled={cpBusy}
                  data-testid="content-pulse-autopilot-toggle" className="rounded-sm"
                  onClick={() => saveCp({ autopilot: !cp.config.autopilot })}>
                  {cp.config.autopilot ? "On" : "Off"}
                </Button>
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                <div className="text-sm">Weekly reminders</div>
                <Button size="sm" variant={cp.config.enabled ? "default" : "outline"} disabled={cpBusy}
                  data-testid="content-pulse-enable-toggle" className="rounded-sm"
                  onClick={() => saveCp({ enabled: !cp.config.enabled })}>
                  {cp.config.enabled ? "On" : "Off"}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Posts per week</Label>
                  <Input type="number" min={1} max={21} data-testid="content-pulse-target"
                    value={cp.config.weekly_target}
                    onChange={(e) => setCp((c) => ({ ...c, config: { ...c.config, weekly_target: parseInt(e.target.value || "0") } }))} />
                </div>
                <div>
                  <Label className="text-xs">Reminder email</Label>
                  <Input data-testid="content-pulse-email" value={cp.config.email_to}
                    onChange={(e) => setCp((c) => ({ ...c, config: { ...c.config, email_to: e.target.value } }))} />
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" onClick={() => saveCp({ weekly_target: cp.config.weekly_target, email_to: cp.config.email_to })}
                  disabled={cpBusy} data-testid="content-pulse-save-btn" className="rounded-sm">
                  <Save className="w-3.5 h-3.5 mr-1.5" />Save
                </Button>
                <Button size="sm" variant="outline" onClick={runCp} disabled={cpBusy}
                  data-testid="content-pulse-run-btn" className="rounded-sm">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" />{cpBusy ? "Working…" : "Draft this week's posts now"}
                </Button>
              </div>
            </>
          ) : (
            <p className="text-xs text-muted-foreground italic">Loading…</p>
          )}
        </div>

        <div className="lg:col-span-2 data-card space-y-3" data-testid="facebook-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <svg className="w-4 h-4 text-[#1877F2]" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.07C24 5.4 18.63 0 12 0S0 5.4 0 12.07C0 18.1 4.39 23.1 10.13 24v-8.44H7.08v-3.49h3.05V9.41c0-3.02 1.79-4.69 4.53-4.69 1.31 0 2.68.24 2.68.24v2.97h-1.51c-1.49 0-1.96.93-1.96 1.89v2.25h3.33l-.53 3.49h-2.8V24C19.61 23.1 24 18.1 24 12.07z"/></svg>
              <span className="label-eyebrow">Facebook Page — cross-post &amp; grow</span>
            </div>
            {fb && (
              <span data-testid="facebook-status"
                className={`text-[0.7rem] px-2 py-1 rounded-sm border ${fb.connected
                  ? "border-emerald-500/40 text-emerald-400 bg-emerald-500/10"
                  : "border-amber-accent/40 text-amber-accent bg-amber-accent/10"}`}>
                {fb.connected ? `● Connected: ${fb.page_name || fb.page_id}` : "Not connected"}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">
            Cross-posts your Content Pulse posts to Facebook and can run "Follow us on LinkedIn 👉" nudges to your 11k FB audience.
          </p>
          {!fb ? (
            <p className="text-xs text-muted-foreground italic">Loading…</p>
          ) : fb.connected ? (
            <>
              <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                <div className="text-sm">Cross-post Content Pulse to Facebook</div>
                <Button size="sm" variant={fb.cross_post ? "default" : "outline"} disabled={fbBusy}
                  data-testid="facebook-crosspost-toggle" className="rounded-sm"
                  onClick={() => fbPref({ cross_post: !fb.cross_post })}>{fb.cross_post ? "On" : "Off"}</Button>
              </div>
              <div className="flex items-center justify-between border border-border rounded-sm px-3 py-2">
                <div>
                  <div className="text-sm">Monthly "Follow us on LinkedIn" post</div>
                  <div className="text-[0.7rem] text-muted-foreground">
                    Auto-posts a CTA to your Facebook Page once a month.
                    {fb.last_cta_at ? ` Last sent ${new Date(fb.last_cta_at).toLocaleDateString()}.` : " Not sent yet."}
                  </div>
                </div>
                <Button size="sm" variant={fb.cta_enabled ? "default" : "outline"} disabled={fbBusy}
                  data-testid="facebook-cta-toggle" className="rounded-sm shrink-0"
                  onClick={() => fbPref({ cta_enabled: !fb.cta_enabled })}>{fb.cta_enabled ? "On" : "Off"}</Button>
              </div>
              <div className="border border-border rounded-sm px-3 py-2 space-y-1.5">
                <div className="text-sm">LinkedIn page link (used in the CTA post)</div>
                <div className="text-[0.7rem] text-muted-foreground">
                  Followers tap this to follow you on LinkedIn. Paste your exact company page URL.
                </div>
                <div className="flex gap-2 items-center">
                  <input data-testid="facebook-linkedin-url-input" type="url" value={fbLinkedin}
                    onChange={(e) => setFbLinkedin(e.target.value)}
                    placeholder="https://www.linkedin.com/company/your-company/"
                    className="flex h-9 flex-1 rounded-sm border border-border bg-background px-2 text-sm" />
                  <Button size="sm" variant="outline" className="rounded-sm shrink-0" disabled={fbBusy || !fbLinkedin.trim()}
                    data-testid="facebook-linkedin-url-save"
                    onClick={() => fbPref({ linkedin_url: fbLinkedin.trim() }).then(() => toast.success("LinkedIn link saved"))}>Save link</Button>
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="outline" className="rounded-sm" disabled={fbBusy}
                  onClick={() => fbAction("test", "Test post published to Facebook")} data-testid="facebook-test-btn">Send test post</Button>
                <Button size="sm" variant="outline" className="rounded-sm" disabled={fbBusy}
                  onClick={() => fbAction("cta", "‘Follow us on LinkedIn’ post published")} data-testid="facebook-cta-btn">Post "Follow us on LinkedIn"</Button>
                <Button size="sm" variant="outline" className="rounded-sm text-destructive" disabled={fbBusy}
                  onClick={() => fbAction("disconnect", "Facebook disconnected")} data-testid="facebook-disconnect-btn">Disconnect</Button>
              </div>
              {fb.pages?.length > 1 && (
                <div className="rounded-sm border border-border p-3 space-y-2" data-testid="facebook-switch-page">
                  <div className="text-[0.7rem] text-muted-foreground">
                    Posting to the wrong Page? Pick the correct one (e.g. the {workspace?.name || "company"} Page) below.
                  </div>
                  <div className="flex flex-wrap gap-2 items-center">
                    <select data-testid="facebook-page-select"
                      className="flex h-9 rounded-sm border border-border bg-background px-2 text-sm min-w-[240px]"
                      value={fbPageChoice} onChange={(e) => setFbPageChoice(e.target.value)}>
                      <option value="">Choose a Page…</option>
                      {fb.pages.map((p) => (
                        <option key={p.id} value={p.id}>{p.name || p.id}</option>
                      ))}
                    </select>
                    <Button size="sm" variant="outline" className="rounded-sm" disabled={fbBusy || !fbPageChoice}
                      onClick={selectFbPage} data-testid="facebook-page-select-btn">Use this Page</Button>
                  </div>
                </div>
              )}
            </>
          ) : fb.needs_pick && fb.pages?.length ? (
            <div className="rounded-sm border border-amber-accent/30 bg-amber-accent/5 p-3 space-y-2" data-testid="facebook-pick">
              <div className="text-sm font-medium">Which Page should LogiGrowth publish to?</div>
              <div className="text-[0.7rem] text-muted-foreground">
                We found {fb.pages.length} Page(s) on your Facebook. Pick the <strong>{workspace?.name || "correct company"}</strong> Page — not your personal profile.
                {fb.has_ungranted_pages && (
                  <span className="block mt-1 text-amber-600">
                    Pages marked "needs Facebook access" are visible but not yet granted to LogiGrowth. Pick one and you'll be sent to Facebook to grant it (tick it on the "Pages you manage" screen).
                  </span>
                )}
              </div>
              <div className="flex flex-wrap gap-2 items-center">
                <select data-testid="facebook-pick-select"
                  className="flex h-9 rounded-sm border border-border bg-background px-2 text-sm min-w-[240px]"
                  value={fbPageChoice} onChange={(e) => setFbPageChoice(e.target.value)}>
                  <option value="">Choose the {workspace?.name || "company"} Page…</option>
                  {fb.pages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {(p.name || p.id)}{p.connectable === false ? " — needs Facebook access" : ""}
                    </option>
                  ))}
                </select>
                <Button size="sm" className="rounded-sm bg-[#1877F2] hover:bg-[#1877F2]/90 text-white"
                  disabled={fbBusy || !fbPageChoice} onClick={selectFbPage} data-testid="facebook-pick-btn">
                  {fbBusy ? "Working…"
                    : ((fb.pages.find((p) => p.id === fbPageChoice)?.connectable === false)
                        ? "Grant on Facebook" : "Connect this Page")}
                </Button>
              </div>
              <p className="text-[0.7rem] text-muted-foreground">
                Don't see the {workspace?.name || "right"} Page (or getting a token error)? Click <button className="underline" onClick={startFbOauth}>Connect with Facebook</button> again and be sure to <strong>tick that Page</strong> on the "Pages you manage" screen so LogiGrowth can post to it.
              </p>
            </div>
          ) : (
            <>
              {!fb.app_configured && (
                <div className="rounded-sm border border-amber-accent/30 bg-amber-accent/5 p-3 space-y-2" data-testid="facebook-appcreds">
                  <div className="text-[0.7rem] text-muted-foreground">One-time on this site: enter your Meta App ID + App Secret so the button can connect.</div>
                  <div className="grid grid-cols-2 gap-3">
                    <Input data-testid="facebook-appid-input" value={fbForm.app_id || ""}
                      onChange={(e) => setFbForm((f) => ({ ...f, app_id: e.target.value }))} placeholder="App ID (e.g. 2016452722323839)" />
                    <Input data-testid="facebook-appsecret-input" value={fbForm.app_secret}
                      onChange={(e) => setFbForm((f) => ({ ...f, app_secret: e.target.value }))} placeholder="App Secret" />
                  </div>
                  <Button size="sm" variant="outline" onClick={saveFbAppCreds} disabled={fbBusy} data-testid="facebook-appcreds-save" className="rounded-sm">
                    {fbBusy ? "Saving…" : "Save app credentials"}
                  </Button>
                </div>
              )}
              <Button size="sm" onClick={startFbOauth} disabled={fbBusy || !fb.app_configured} data-testid="facebook-oauth-btn"
                className="rounded-sm bg-[#1877F2] hover:bg-[#1877F2]/90 text-white">
                {fbBusy ? "Opening Facebook…" : "Connect with Facebook (recommended)"}
              </Button>
              <p className="text-[0.7rem] text-muted-foreground">
                One click → log in → approve → <strong>tick the {workspace?.name || "company"} Page</strong> on the Pages screen. That's it.
              </p>
              <details className="text-[0.7rem] text-muted-foreground">
                <summary className="cursor-pointer">or paste a Page token manually</summary>
                <div className="space-y-2 mt-2">
                  <Input data-testid="facebook-token-input" value={fbForm.page_token}
                    onChange={(e) => setFbForm((f) => ({ ...f, page_token: e.target.value }))}
                    placeholder="EAAB… (long-lived Page token)" />
                  <div className="grid grid-cols-2 gap-3">
                    <Input data-testid="facebook-pageid-input" value={fbForm.page_id}
                      onChange={(e) => setFbForm((f) => ({ ...f, page_id: e.target.value }))} placeholder="Page ID (optional)" />
                    <Input data-testid="facebook-secret-input" value={fbForm.app_secret}
                      onChange={(e) => setFbForm((f) => ({ ...f, app_secret: e.target.value }))} placeholder="App Secret (optional)" />
                  </div>
                  <Button size="sm" variant="outline" onClick={connectFb} disabled={fbBusy} data-testid="facebook-connect-btn" className="rounded-sm">
                    {fbBusy ? "Connecting…" : "Connect with token"}
                  </Button>
                </div>
              </details>
            </>
          )}
        </div>

        <div className="lg:col-span-2 data-card space-y-3" data-testid="suppression-card">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2">
              <ShieldOff className="w-4 h-4 text-amber-accent" />
              <span className="label-eyebrow">Suppression / Unsubscribes</span>
              <span className="text-xs text-muted-foreground">{supp.total} on list</span>
            </div>
            <div className="relative">
              <Search className="w-3.5 h-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={suppSearch}
                onChange={(e) => setSuppSearch(e.target.value)}
                placeholder="Search email…"
                className="h-8 pl-8 w-56 text-xs"
                data-testid="suppression-search"
              />
            </div>
          </div>
          <p className="text-xs text-muted-foreground">
            Anyone here is excluded from ALL outreach (email + LinkedIn). People unsubscribe via the
            footer link in every email; you can also add or remove addresses manually (e.g. GDPR requests).
          </p>
          <div className="flex gap-2">
            <Input
              value={newSupp}
              onChange={(e) => setNewSupp(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addSuppression()}
              placeholder="email@company.com"
              className="h-9 text-sm"
              data-testid="suppression-add-input"
            />
            <Button
              onClick={addSuppression}
              disabled={suppBusy || !newSupp.trim()}
              className="rounded-sm shrink-0"
              data-testid="suppression-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Suppress
            </Button>
          </div>
          <div className="border border-border rounded-sm divide-y divide-border max-h-72 overflow-y-auto" data-testid="suppression-list">
            {supp.suppressions.length === 0 ? (
              <p className="text-xs text-muted-foreground italic p-4 text-center">
                {suppSearch ? "No matching addresses." : "No unsubscribes yet."}
              </p>
            ) : (
              supp.suppressions.map((s) => (
                <div key={s.email} className="flex items-center justify-between gap-3 px-3 py-2 text-xs" data-testid={`suppression-row-${s.email}`}>
                  <div className="min-w-0">
                    <div className="font-mono truncate">{s.email}</div>
                    <div className="text-[0.65rem] text-muted-foreground">
                      {[s.source, s.reason && s.reason !== s.source ? s.reason : null, (s.created_at || "").slice(0, 10)].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 text-xs text-muted-foreground hover:text-foreground shrink-0"
                    onClick={() => removeSuppression(s.email)}
                    data-testid={`suppression-remove-${s.email}`}
                  >
                    Re-subscribe
                  </Button>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="lg:col-span-2 data-card space-y-3" data-testid="blog-ingest-card">
          <div className="flex items-center gap-2">
            <Rss className="w-4 h-4 text-signal-500" />
            <span className="label-eyebrow">Blog / SEO ingestion</span>
            {blog.configured && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
          </div>
          <p className="text-xs text-muted-foreground">
            Push published articles & long posts to your SEO blog app. Get the endpoint URL + API key from that app's "Blog Ingestion API" panel. Articles auto-push on publish; you can also re-push manually from the Articles page.
          </p>
          <Row label="Endpoint URL">
            <Input
              data-testid="blog-endpoint-input"
              placeholder="https://your-seo-app.com/api/blog/ingest"
              value={blog.endpoint_url || ""}
              onChange={(e) => setBlog({ ...blog, endpoint_url: e.target.value })}
            />
          </Row>
          <Row label="API Key">
            <Input
              data-testid="blog-apikey-input"
              type="password"
              placeholder={blog.api_key_set ? `Saved (ending ${blog.api_key_hint}) — leave blank to keep` : "ep_blog_…"}
              value={blogKey}
              onChange={(e) => setBlogKey(e.target.value)}
            />
          </Row>
          <div>
            <Button size="sm" onClick={saveBlog} disabled={blogBusy} data-testid="blog-save-btn" className="rounded-sm">
              <Save className="w-3.5 h-3.5 mr-1.5" />{blogBusy ? "Saving…" : "Save blog ingestion"}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2 data-card space-y-3" data-testid="directory-import-card">
          <div className="flex items-center gap-2">
            <Users className="w-4 h-4 text-signal-500" />
            <span className="label-eyebrow">Partner network directory (import agents)</span>
            {dir.configured && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
          </div>
          <p className="text-xs text-muted-foreground">
            Connect your partner-directory app's export API to pull whole logistics networks (Seajet, WFN, etc.) as campaign prospects in one click. Paste the export endpoint + its API key. Then, on any campaign → "Add prospects" → "Import from network directory".
          </p>
          <Row label="Export URL">
            <Input
              data-testid="directory-url-input"
              placeholder="https://your-directory-app.emergent.host/api/partner-directory/export"
              value={dir.export_url || ""}
              onChange={(e) => setDir({ ...dir, export_url: e.target.value })}
            />
          </Row>
          <Row label="API Key">
            <Input
              data-testid="directory-apikey-input"
              type="password"
              placeholder={dir.api_key_set ? `Saved (ending ${dir.api_key_hint}) — leave blank to keep` : "epk_…"}
              value={dirKey}
              onChange={(e) => setDirKey(e.target.value)}
            />
          </Row>
          <div>
            <Button size="sm" onClick={saveDir} disabled={dirBusy} data-testid="directory-save-btn" className="rounded-sm">
              <Save className="w-3.5 h-3.5 mr-1.5" />{dirBusy ? "Saving…" : "Save partner directory"}
            </Button>
          </div>
        </div>

        <div className="lg:col-span-2 data-card space-y-3 border-destructive/40" data-testid="danger-zone-card">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-destructive" />
            <span className="label-eyebrow text-destructive">Danger zone</span>
          </div>
          <p className="text-xs text-muted-foreground">
            Clean up sample/demo data that was auto-seeded into this workspace (prospects, campaigns,
            conversations, analytics, agent &amp; sequencer runs). Use this to clear out the demo
            pipeline on a freshly deployed production workspace.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 pt-1">
            <Button
              variant="outline"
              className="rounded-sm border-destructive/40 text-destructive hover:bg-destructive/10"
              onClick={() => { setPurgeAll(false); setPurgeOpen(true); }}
              data-testid="purge-demo-only-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Remove demo data only
            </Button>
            <Button
              variant="ghost"
              className="rounded-sm text-destructive hover:bg-destructive/10"
              onClick={() => { setPurgeAll(true); setPurgeOpen(true); }}
              data-testid="purge-all-btn"
            >
              <Trash2 className="w-4 h-4 mr-1.5" /> Wipe ALL prospects &amp; pipeline
            </Button>
          </div>
          <p className="text-[0.6rem] text-muted-foreground">
            &quot;Remove demo data only&quot; keeps real leads sourced from Apollo / LinkedIn. &quot;Wipe ALL&quot; deletes every prospect. Owner-only. This cannot be undone.
          </p>
        </div>
      </div>

      <AlertDialog open={purgeOpen} onOpenChange={setPurgeOpen}>
        <AlertDialogContent data-testid="purge-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {purgeAll ? "Wipe ALL prospects & pipeline?" : "Remove demo data?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {purgeAll
                ? "This permanently deletes EVERY prospect (including real Apollo/LinkedIn leads), plus all campaigns, conversations, analytics, and agent/sequencer runs in this workspace. This cannot be undone."
                : "This permanently deletes demo-seeded prospects and all campaigns, conversations, analytics, and agent/sequencer runs. Real leads sourced from Apollo / LinkedIn are kept. This cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purge-cancel-btn" disabled={purging}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              data-testid="purge-confirm-btn"
              disabled={purging}
              onClick={(e) => { e.preventDefault(); runPurge(); }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {purging ? "Purging..." : (purgeAll ? "Yes, wipe everything" : "Yes, remove demo data")}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}

function Row({ label, children }) {
  return (
    <div>
      <Label className="text-xs mb-1 block">{label}</Label>
      {children}
    </div>
  );
}
